import React, { FC, useMemo } from 'react';
import { get } from 'lodash';
import { useFormikContext } from 'formik';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import Image from 'vibo-ui/Image';
import Switch from 'vibo-ui/Switch';
import Scrollbar from 'vibo-ui/Scrollbar';
import InputLabel from 'components/common/InputLabel';
import PublicLabel from 'components/labels/PublicLabel';
import ContentArea from 'components/common/ContentArea';
import SpotifyButton from 'components/buttons/SpotifyButton';
import FormTextarea from 'components/form/inputs/FormTextarea';
import Icon, { IconmoonFont } from 'vibo-ui/Icon';
import Button, { ButtonDecor } from 'vibo-ui/Button';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { useMomentShort } from 'services/moment';
import { MAX_DESCRIPTION_LENGTH } from 'services/constants';
import { openErrorNotification } from 'graphql/hooks/common';

import { SYNC_SPOTIFY_PLAYLIST_WITH_SONG_IDEAS } from 'graphql/mutations/songIdeas';

import { Modals } from 'types/enums';
import { SongIdeasDetailsProps } from './interfaces';
import { PickFilesModalProps } from 'components/modals/PickFilesModal';
import { ConfirmActionModalProps } from 'components/modals/ConfirmActionModal';
import { SyncSpotifyPlaylistModalProps } from 'components/modals/SyncSpotifyPlaylistModal';

import useStyles from './style';

const SongIdeasDetailsCard: FC<SongIdeasDetailsProps> = ({ songIdeas, canEdit }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { openModal } = useModal();

  const momentShort = useMomentShort('full');

  const formik = useFormikContext<UpdateSongIdeasForm>();

  const openPickImageModal = () =>
    !formik.isSubmitting &&
    songIdeas.isOwner &&
    openModal<PickFilesModalProps>({
      key: Modals.pickImage,
      props: {
        fileSrc: songIdeas.coverPhoto.url,
        title: t('playlistImage'),
        onChange: async (_, photo) => {
          formik.setFieldValue('coverPhoto', {
            photo: photo || '',
          });
          formik.setSubmitting(true);
        },
      },
    });
  const openConfirmChangeSync = (enable: boolean) =>
    openModal<ConfirmActionModalProps>({
      key: Modals.confirmAction,
      props: {
        title: 'Turn on Spotify sync',
        children: enable
          ? 'Turning on Spotify sync will reset the playlist to match your Spotify version, discarding your edits.'
          : 'Are you sure you want to turn off Spotify sync?',
        submit: {
          text: 'Continue',
          onClick: () => {
            formik.setFieldValue('syncSettings.spotify.isSynchronized', enable);
            enable && syncSpotifyPlaylistWithSongIdeas();
          },
        },
      },
    });
  const openSyncSpotifyPlaylistModal = () =>
    openModal<SyncSpotifyPlaylistModalProps>({
      key: Modals.syncSpotifyPlaylist,
      props: {
        songIdeasId: songIdeas._id,
      },
    });

  const [syncSpotifyPlaylistWithSongIdeas, { loading }] = useMutation(
    SYNC_SPOTIFY_PLAYLIST_WITH_SONG_IDEAS,
    {
      onCompleted: data => {
        const response = get(
          data,
          'syncSpotifyPlaylistWithSongIdeas'
        ) as SyncSpotifyPlaylistResponse;

        response.reason && openErrorNotification(response.reason);
      },
      onError,
      variables: {
        songIdeasId: songIdeas._id,
      },
      refetchQueries: ['getSongIdeasSongs'],
    }
  );

  const creatorName = `${songIdeas.creator?.firstName ?? ''} ${songIdeas.creator?.lastName ??
    ''}`.trim();
  const playlistPhoto = songIdeas?.coverPhoto?.url || '';
  const spotify = songIdeas.syncSettings?.spotify;

  const lastUpdated = useMemo(() => momentShort(songIdeas.updatedAt).fromNow(), [
    songIdeas.updatedAt,
  ]);
  const lastSynced = useMemo(() => momentShort(spotify.lastSynced).fromNow(), [spotify.lastSynced]);

  return (
    <Scrollbar className={classes.scrollbar}>
      <ContentArea className={classes.playlistCard} shadowed>
        <Image
          src={playlistPhoto}
          onClick={openPickImageModal}
          isUpdating={formik.isSubmitting}
          isPickable={songIdeas.isOwner}
          gradientId={songIdeas._id}
          label={songIdeas.isPublic ? <PublicLabel className="whiteBg" /> : null}
        >
          <Icon icon={IconmoonFont['musicNoteDoubleOutlined-24']} disableAutoFontSize />
        </Image>
        <FormTextarea
          placeholder={`${t('playlistDescription')}...`}
          disabled={!canEdit}
          minRows={1}
          maxLength={MAX_DESCRIPTION_LENGTH}
          className={classes.playlistDescription}
          name="description"
          showMaxLength
          withUnderline
        />
        {creatorName ? (
          <InputLabel text="Creator" view="horizontal">
            <b>{creatorName}</b>
          </InputLabel>
        ) : null}
        <InputLabel text={t('lastUpdated')} view="horizontal">
          <b>{lastUpdated}</b>
        </InputLabel>
      </ContentArea>
      {songIdeas.isOwner && !!spotify.link ? (
        <ContentArea className={classes.playlistSync} shadowed>
          <InputLabel text="Original playlist">
            <SpotifyButton
              onClick={() => window.open(spotify.link, '_blank')}
              text={spotify.playlistName}
              role="link"
              displayType="link"
              size="xs"
            />
          </InputLabel>
          <InputLabel text="Synchronize songs with Spotify" view="horizontal">
            <Switch
              onChange={openConfirmChangeSync}
              checked={formik.values.syncSettings?.spotify?.isSynchronized}
              loading={loading}
            />
          </InputLabel>
          <InputLabel text="Last synchronized" view="horizontal">
            <b>{spotify.lastSynced ? lastSynced : 'Never'}</b>
          </InputLabel>
          <Button
            onClick={openSyncSpotifyPlaylistModal}
            prefixIcon={IconmoonFont['sync-16']}
            loading={loading}
            className={ButtonDecor.centrate}
            displayType="bordered"
            shape="round"
            size="lg"
          >
            Sync now
          </Button>
          {spotify.error ? <div className="error">{spotify.error}</div> : null}
        </ContentArea>
      ) : null}
    </Scrollbar>
  );
};

export default SongIdeasDetailsCard;
